import { isRestOfWorld } from './i18nHelpers';
export const formatDisplayPrice = (price, currency, formatOptions) => {
    return formatPrice(price, currency, {
        ...formatOptions,
        showCents: false,
    });
};
export const formatCartPrice = (price, currency, formatOptions) => {
    return formatPrice(price, currency, {
        ...formatOptions,
        showCents: true,
        roundUp: false,
        roundDown: false,
    });
};
export const formatPriceWithSiteValues = (price, siteValues, formatOptions) => {
    const { currency, language, region } = siteValues;
    const country = isRestOfWorld(currency, region) ? region : undefined;
    return formatPrice(price, currency, { country, language, ...formatOptions });
};
const generateFormattingLocale = (currency, language, country) => {
    const countryPriceFormatOverrides = {
        es: { default: 'US', eur: 'ES' },
        it: { default: 'IT' },
        pt: { default: 'PT' },
    };
    // forcedFormattingCountry is built to achieve ideal price formatting for the new latin langs (es, it, pt)
    // Locale here is not representative of users actual locale. (Example: pt-EU (€ 12) becomes pt-PT (12 €))
    let locale = country && language ? `${language}-${country}` : language;
    if (['es', 'it', 'pt'].includes(language ?? '')) {
        const forcedFormattingCountry = countryPriceFormatOverrides[language ?? ''][currency] ??
            countryPriceFormatOverrides[language ?? ''].default;
        locale = `${language}-${forcedFormattingCountry}`;
    }
    return locale;
};
export const formatPrice = (price, currency, formatOptions) => {
    const { country, language, inCents = true, roundUp = false, roundDown = false, showCents, } = formatOptions ?? {};
    let priceInDollars = inCents ? Math.round(price) / 100 : price;
    if (!showCents && roundUp) {
        priceInDollars = Math.ceil(priceInDollars);
    }
    else if (!showCents && roundDown) {
        priceInDollars = Math.floor(priceInDollars);
    }
    const fractionDigits = showCents || priceInDollars % 1 !== 0 ? 2 : 0;
    const locale = generateFormattingLocale(currency, language, country);
    return Intl.NumberFormat(locale, {
        currency,
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
        style: 'currency',
    }).format(priceInDollars);
};
export const splitPrice = ({ country, currency, formattedPrice, language, }) => {
    const locale = generateFormattingLocale(currency, language, country);
    const decimalSeparator = Intl.NumberFormat(locale, { currency, style: 'currency' })
        .formatToParts(1.1)
        .find((part) => part.type === 'decimal')?.value || '.';
    return {
        dollarAmount: formattedPrice.split(decimalSeparator)[0],
        centsAmount: formattedPrice.split(decimalSeparator)[1],
        decimalSeparator,
    };
};
/**
 * Takes a numeric amount, applies the given exchange rate, rounds to the provided nearest value, formats according to provided currency (taking into consideration language)
 * @param totalAmount (ex. 10000 = $100 USD)
 * @param exchangeRate (ex. 83, 1 USD = 83 INR)
 * @param exchangeCurrency (ex. INR)
 * @param exchangeRoundingNearest (ex. 50000 (500 INR), such that 12005 rounds to 12500)
 * @param language
 * @returns formatted price in the provided currency (ex. ₹8,300)
 */
export const formatExchangeCurrencyAmount = (totalAmount, exchangeRate, exchangeCurrency, exchangeRoundingNearest, language) => {
    const estimatedTotalAmount = totalAmount * exchangeRate; //ex. 2216100 INR
    const roundedTotalAmount = Math.ceil(estimatedTotalAmount / exchangeRoundingNearest) *
        exchangeRoundingNearest; //ex. 2250000 INR
    return Intl.NumberFormat(language, {
        currency: exchangeCurrency,
        style: 'currency',
        maximumFractionDigits: exchangeRoundingNearest ? 0 : 2,
    }).format(roundedTotalAmount / 100);
};
